import { AcceptanceToolkit, AvatarDropdown, AvatarName, SelectLang } from "@/components";
import { getFirstMenuPath, getMenus } from "@/services/global";
import attachMenuIcon from "@/utils/enumMenu";
import { normalizeUserAvatar } from "@/utils/defaultAvatar";
import * as storage from "@/utils/storage";
import { getTenantSwitchContext } from "@/utils/tokenContext";
import { FullscreenOutlined, LinkOutlined } from "@ant-design/icons";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { SettingDrawer } from "@ant-design/pro-components";
import "@ant-design/v5-patch-for-react-19";
import { Tag } from "antd";
import type { RunTimeLayoutConfig } from "@umijs/max";
import { history, Link, matchRoutes } from "@umijs/max";
import defaultSettings from "../config/defaultSettings";
import logo from "../public/logo-login.png";
const showDevTools =
  process.env.NODE_ENV === "development" ||
  process.env.CI ||
  process.env.SHOW_DEV_TOOLS === "true";
const loginPath = "/user/login";
const nonMenuRouteWhiteList = ["/big-screen"];
let runtimeBasename = "/";

const getBigScreenUrl = () => `${window.location.origin}/epbg`;

const normalizeBasename = (basename?: string) => {
  if (!basename || basename === "/") return "/";
  return basename.replace(/\/+$/, "");
};

export function onRouteChange({
  basename,
}: {
  basename: string;
}) {
  runtimeBasename = normalizeBasename(basename);
}

const isPlatformUser = (user?: API.CurrentUser) => {
  if (!user) return false;
  const currentUser = user as API.CurrentUser & {
    userType?: number | string;
    roleCode?: string;
    access?: string;
  };
  return (
    String(currentUser.userType ?? "") === "1" ||
    String(currentUser.roleCode ?? "").toUpperCase().includes("PLATFORM") ||
    String(currentUser.access ?? "").toLowerCase() === "admin"
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = () => {
    const currentUser = storage.get("current-user");
    if (currentUser) {
      return normalizeUserAvatar(currentUser);
    } else {
      console.log("----进入了登录页面-----");

      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, "/user/register", "/user/register-result"].includes(
      location.pathname
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

/**
 * 递归收集所有路由路径
 * @param menus 菜单数据
 * @returns 所有路径的数组
 */
const collectAllPaths = (menus: any[]): string[] => {
  const paths: string[] = [];

  const traverse = (items: any[]) => {
    if (!Array.isArray(items)) return;

    items.forEach((item) => {
      if (item.path) {
        paths.push(item.path);
      }
      // 递归处理子路由
      if (Array.isArray(item.routes)) {
        traverse(item.routes);
      }
      if (Array.isArray(item.children)) {
        traverse(item.children);
      }
    });
  };

  traverse(menus);
  return paths;
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const currentUser = initialState?.currentUser as
    | (API.CurrentUser & { tenantProjectName?: string })
    | undefined;
  const tenantSwitchContext = getTenantSwitchContext();
  const currentProjectName = String(
    currentUser?.tenantProjectName || tenantSwitchContext?.projectName || "",
  ).trim();
  const canAccessBigScreen = isPlatformUser(currentUser) && !tenantSwitchContext;

  return {
    logo: () => (
      <img src={logo} alt="" style={{ display: "block", width: "36px" }} />
    ),
    onMenuHeaderClick: (event) => {
      event.preventDefault();
      event.stopPropagation();
    },
    itemRender: (route) => (
      <span>{route.title ?? route.breadcrumbName}</span>
    ),
    menu: {
      // 每当 initialState发生修改时重新执行 request
      params: initialState?.currentUser,
      request: async (params, defaultMenuData) => {
        // initialState.currentUser 中包含了所有用户信息
        console.log(params);
        console.log("----接口返回路由表-----");
        if (!params) return defaultMenuData;
        const menuData = await getMenus();
        if (menuData.code === "00000") {
          const rawMenus = menuData.result;
          const normalizedMenus = Array.isArray(rawMenus)
            ? rawMenus
            : rawMenus
            ? [rawMenus]
            : [];

          // 收集所有路由路径
          const allPaths = collectAllPaths(normalizedMenus);
          // 使用 Umi 路由匹配处理 basename，避免部署路径变化影响菜单判断。
          const isCurrentPathInMenu = Boolean(
            matchRoutes(
              [...allPaths, ...nonMenuRouteWhiteList].map((path) => ({ path })),
              history.location,
              runtimeBasename,
            ),
          );
          // 如果当前路径不在路由表中，才跳转到第一个菜单
          if (!isCurrentPathInMenu && normalizedMenus.length > 0) {
            const firstMenu = getFirstMenuPath(normalizedMenus);

            if (firstMenu) {
              setTimeout(() => {
                history.replace(firstMenu);
              }, 100);
            }
          }

          //返回处理后的菜单数据
          return attachMenuIcon(normalizedMenus);
        }
        return defaultMenuData;
      },
    },
    actionsRender: () => [
      ...(currentProjectName
        ? [
            <Tag
              key="current-project"
              color="geekblue"
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginInlineEnd: 0,
                padding: "4px 10px",
                fontSize: 14,
                fontWeight: 600,
                whiteSpace: "nowrap",
                cursor: "default",
              }}
            >
              {currentProjectName}
            </Tag>,
          ]
        : []),
      // <Question key="doc" />,
      ...(canAccessBigScreen
        ? [
            <a
              key="big-screen"
              href={getBigScreenUrl()}
              target="_blank"
              rel="noopener noreferrer"
              title="大屏"
            >
              <FullscreenOutlined />
            </a>,
          ]
        : []),
      // <Link
      //   key="big-screen"
      //   to="/big-screen"
      //   target="_blank"
      //   title="大屏"
      //   style={{
      //     width: 28,
      //     height: 28,
      //     display: "inline-flex",
      //     alignItems: "center",
      //     justifyContent: "center",
      //     color: "#64748b",
      //     borderRadius: 6,
      //   }}
      // >
      //   <FullscreenOutlined />
      // </Link>,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: normalizeUserAvatar(initialState?.currentUser)?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: false,
    // footerRender: () => <Footer />,
    onPageChange: () => {
      console.log("----onPageChange-----");
      const { location } = history;
      const currentUser = storage.get("current-user");
      // 如果没有登录，重定向到 login
      if (!currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
        left: 85,
        bottom: 100,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
        bottom: -68,
        right: -45,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
        bottom: 0,
        left: 0,
        width: "331px",
      },
    ],
    links: showDevTools
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {showDevTools && <AcceptanceToolkit />}
          {showDevTools && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
// export const request: RequestConfig = {
//   baseURL: 'https://proapi.azurewebsites.net',
//   ...errorConfig,
// };
